import { Button } from "@/components/ui/UIElements/Button";
import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui";
import Modal from "../../ui/UIElements/Modal";
import {
  useDriverManagerDetail,
  useToggleDriverManagerStatus,
  useDeleteDriverManager,
} from "../../../hooks/useDriverManagers";
import Breadcrumb from "../../ui/Navigation/Breadcrumb";
import OverviewTab from "../../ui/Users/OverviewTab";
import DriverGroupTab from "../../ui/Users/DriverGroupTab";
import DeleteConfirmationModal from "../../ui/Modals/DeleteConfirmationModal";
import EditDriverManagerModal from "../../ui/Modals/EditDriverManagerModal";

const DriverManagerDetailPage = ({ initialUser = null }) => {
  const { t } = useTranslation("delivery");
  const { id } = useParams();
  const userId = parseInt(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const toggleUserStatusMutation = useToggleDriverManagerStatus();
  const deleteUserMutation = useDeleteDriverManager();

  // Get user data from navigation state or props
  const userFromState = location.state?.user;
  const userData = initialUser || userFromState;

  // Fetch user data
  const {
    data: currentUser,
    isLoading,
    error,
    isError,
    refetch: refetchUser,
  } = useDriverManagerDetail(userId, userData);

  const handleToggleUserStatus = () => {
    if (currentUser?.enabled) {
      setShowDeactivateModal(true);
    } else {
      handleConfirmToggle();
    }
  };

  const handleConfirmToggle = async () => {
    try {
      const newEnabledStatus = !currentUser.enabled;

      await toggleUserStatusMutation.mutateAsync({
        userId: currentUser.id,
        enabled: newEnabledStatus,
      });

      await refetchUser();

      setShowDeactivateModal(false);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUserMutation.mutateAsync(currentUser.id);
      navigate("/delivery/drivers-managers");
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleBackToDriversManagers = () => {
    navigate("/delivery/drivers-managers");
  };

  if (isLoading) {
    return (
      <div className="ordering-delivery-surface flex min-h-96 items-center justify-center p-4">
        <div className="text-center">
          <div className="border-primary mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 sm:mb-4 sm:h-12 sm:w-12"></div>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("driverManagerDetail.states.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isError || (!isLoading && !currentUser)) {
    return (
      <div className="ordering-delivery-surface px-4 py-8 text-center sm:py-12">
        <Icon
          name="UserX"
          className="text-muted-foreground mx-auto mb-3 h-8 w-8 sm:mb-4 sm:h-12 sm:w-12"
        />
        <h3 className="text-foreground mt-2 text-sm font-medium sm:text-base">
          {t("driverManagerDetail.states.notFoundTitle")}
        </h3>
        <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
          {error?.message ||
            t("driverManagerDetail.states.notFoundDescription")}
        </p>
        <Button
          emphasis="solid"
          intent="brand"
          size="touch"
          onClick={handleBackToDriversManagers}
          className="mt-3 px-3 py-2 transition-colors sm:mt-4 sm:px-4"
        >
          {t("driverManagerDetail.actions.backToDriversManagers")}
        </Button>
      </div>
    );
  }

  const name =
    currentUser?.name && currentUser?.lastname
      ? `${currentUser.name} ${currentUser.lastname}`
      : currentUser?.name || t("driverManagerDetail.fallbacks.unknownUser");

  const initials =
    currentUser?.name && currentUser?.lastname
      ? `${currentUser.name.charAt(0)}${currentUser.lastname.charAt(
          0,
        )}`.toUpperCase()
      : currentUser?.name?.charAt(0)?.toUpperCase() || "U";

  const breadcrumbItems = [
    {
      label: t("driverManagerDetail.breadcrumb.delivery"),
      path: "/delivery/drivers-managers",
      icon: "Truck",
    },
    {
      label: name,
      active: true,
      disabled: true,
    },
  ];

  return (
    <div className="ordering-delivery-surface space-y-4 sm:space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* User Profile Header */}
      <div className="border-border bg-background rounded-lg border">
        <div className="border-border border-b p-4 sm:p-6">
          <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 flex-1 items-center space-x-3 sm:space-x-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-16 sm:w-16">
                {currentUser.photo ? (
                  <img
                    src={currentUser.photo}
                    alt={name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className={`bg-primary text-primary-foreground flex h-full w-full items-center justify-center text-lg font-semibold sm:text-2xl ${
                    currentUser.photo ? "hidden" : "flex"
                  }`}
                >
                  {initials}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-foreground truncate text-lg font-bold sm:text-2xl">
                  {name}
                </h1>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <Button
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                  aria-label={t("common:actions.edit")}
                  onClick={() => setShowEditModal(true)}
                  className="flex cursor-pointer items-center justify-center space-x-2 px-3 py-2 transition-colors sm:px-4"
                >
                  <Icon name="Edit" className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">
                    {t("common:actions.edit")}
                  </span>
                </Button>
                <Button
                  emphasis="solid"
                  intent={currentUser.enabled ? "danger" : "success"}
                  size="touch"
                  onClick={handleToggleUserStatus}
                  disabled={toggleUserStatusMutation.isPending}
                  className="flex items-center justify-center gap-2 px-3 sm:px-4"
                >
                  <Icon
                    name={currentUser.enabled ? "UserX" : "UserCheck"}
                    className="h-3 w-3 sm:h-4 sm:w-4"
                  />
                  <span className="hidden sm:inline">
                    {toggleUserStatusMutation.isPending
                      ? t("driverManagerDetail.actions.updating")
                      : currentUser.enabled
                        ? t("driverManagerDetail.actions.deactivate")
                        : t("driverManagerDetail.actions.activate")}
                  </span>
                  <span className="sm:hidden">
                    {toggleUserStatusMutation.isPending
                      ? t("driverManagerDetail.actions.updating")
                      : currentUser.enabled
                        ? t("driverManagerDetail.actions.deactivate")
                        : t("driverManagerDetail.actions.activate")}
                  </span>
                </Button>
                <Button
                  emphasis="solid"
                  intent="danger"
                  size="touch"
                  aria-label={t("driverManagerDetail.actions.delete")}
                  onClick={() => setShowDeleteModal(true)}
                  disabled={deleteUserMutation.isPending}
                  className="flex cursor-pointer items-center justify-center space-x-2 px-3 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
                >
                  <Icon name="Trash2" className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">
                    {t("driverManagerDetail.actions.delete")}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-border border-b px-4 sm:px-6">
          <nav className="flex space-x-4 overflow-x-auto sm:space-x-8">
            <Button
              variant="ghost"
              onClick={() => setActiveTab("overview")}
              className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                activeTab === "overview"
                  ? "border-primary text-white"
                  : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"
              }`}
            >
              {t("driverManagerDetail.tabs.overview")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setActiveTab("driver-group")}
              className={`border-b-2 px-1 py-3 text-xs font-medium whitespace-nowrap transition-colors sm:py-4 sm:text-sm ${
                activeTab === "driver-group"
                  ? "border-primary text-white"
                  : "text-muted-foreground hover:border-border hover:text-foreground border-transparent"
              }`}
            >
              {t("driverManagerDetail.tabs.driverGroups")}
            </Button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="p-4 sm:p-6">
          {/* Overview Tab */}
          <div className={activeTab === "overview" ? "block" : "hidden"}>
            <OverviewTab user={currentUser} />
          </div>

          {/* Driver Group Tab */}
          <div className={activeTab === "driver-group" ? "block" : "hidden"}>
            <DriverGroupTab userId={currentUser?.id} />
          </div>
        </div>
      </div>

      {/* Deactivate User Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title={t("driverManagerDetail.deactivateModal.title")}
        subtitle={t("driverManagerDetail.deactivateModal.subtitle")}
        maxWidth="max-w-md"
        className="ordering-delivery-modal"
      >
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="flex-shrink-0">
              <div className="bg-destructive/20 flex h-8 w-8 items-center justify-center rounded-full sm:h-10 sm:w-10">
                <Icon
                  name="AlertTriangle"
                  className="text-destructive h-4 w-4 sm:h-5 sm:w-5"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-foreground text-sm font-medium">
                {t("driverManagerDetail.deactivateModal.confirmTitle", {
                  name,
                })}
              </h3>
              <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
                {t("driverManagerDetail.deactivateModal.description")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            <Button
              emphasis="solid"
              intent="danger"
              size="touch"
              onClick={handleConfirmToggle}
              disabled={toggleUserStatusMutation.isPending}
              className="flex flex-1 items-center justify-center space-x-2 px-3 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
            >
              {toggleUserStatusMutation.isPending ? (
                <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-white sm:h-4 sm:w-4"></div>
              ) : null}
              <span>
                {toggleUserStatusMutation.isPending
                  ? t("driverManagerDetail.deactivateModal.deactivating")
                  : t("driverManagerDetail.actions.deactivate")}
              </span>
            </Button>
            <Button
              emphasis="solid"
              intent="neutral"
              size="touch"
              onClick={() => setShowDeactivateModal(false)}
              disabled={toggleUserStatusMutation.isPending}
              className="text-foreground flex-1 px-3 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
            >
              {t("common:actions.cancel")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t("driverManagerDetail.deleteModal.title")}
        itemName={name}
        itemType={t("driverManagerDetail.deleteModal.itemType")}
        isLoading={deleteUserMutation.isPending}
      />

      {/* Edit Driver Manager Modal */}
      <EditDriverManagerModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          refetchUser();
        }}
        user={currentUser}
      />
    </div>
  );
};

export default DriverManagerDetailPage;
